import { useCallback, useEffect, useRef, useState } from "react";
import { BriefcaseBusiness } from "lucide-react";

import PilotageField from "@/components/pilotage/PilotageField";
import PilotageTable from "@/components/pilotage/PilotageTable";
import { Button } from "@/components/ui/button";
import AppEmptyState from "@/components/ui/AppEmptyState";
import AppErrorState from "@/components/ui/AppErrorState";
import AppLoadingState from "@/components/ui/AppLoadingState";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { getApiErrorMessage, logAppError } from "@/lib/api";
import { getMissionDisplayName } from "@/lib/collabUi";
import {
  emitTimesheetUpdated,
  subscribeTimesheetUpdated,
} from "@/lib/dataEvents";
import {
  EXPENSE_CATEGORIES,
  createExpense,
  createTimesheet,
  listExpenses,
  listMissions,
  listTimesheetMonths,
  updateTimesheet,
} from "@/lib/pilotageApi";
import { formatCurrency, formatDateLabel, formatMonthLabel, toMonthKey } from "@/lib/pilotageUi";
import { effectiveDays, resolveMissionTimesheetFallbackDays } from "@/lib/businessRules";
import { getAvailableMonthsForTimesheet } from "@/lib/timesheetRules";
import TimesheetDailyModal from "@/components/pilotage/TimesheetDailyModal";

const CollabHome = ({ focusSection }) => {
  const { token, user } = useAuth();
  const [missions, setMissions] = useState([]);
  const [selectedMissionId, setSelectedMissionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [timesheetRows, setTimesheetRows] = useState([]);
  const [timesheetsLoading, setTimesheetsLoading] = useState(true);
  const [timesheetError, setTimesheetError] = useState("");
  const [timesheetBusyKey, setTimesheetBusyKey] = useState("");
  const [timesheetSaveStatus, setTimesheetSaveStatus] = useState({});
  const [dailyModalRow, setDailyModalRow] = useState(null);

  const [expenses, setExpenses] = useState([]);
  const [expensesLoading, setExpensesLoading] = useState(true);
  const [expenseError, setExpenseError] = useState("");
  const [expenseBusyId, setExpenseBusyId] = useState("");
  const [expenseForm, setExpenseForm] = useState({
    date: new Date().toISOString().slice(0, 10),
    category: "TRANSPORT",
    amount: "",
    comment: "",
  });

  const expensesSectionRef = useRef(null);

  const selectedMission =
    missions.find((mission) => mission.id === selectedMissionId) || missions[0] || null;

  const loadMissions = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const data = await listMissions();
      setMissions(data);
      setSelectedMissionId((current) => {
        if (data.length === 0) {
          return "";
        }
        if (current && data.some((mission) => mission.id === current)) {
          return current;
        }
        return data[0].id;
      });
    } catch (requestError) {
      setError(getApiErrorMessage(requestError, "Impossible de charger vos missions."));
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTimesheets = useCallback(async (missionId) => {
    if (!missionId) {
      setTimesheetRows([]);
      setTimesheetsLoading(false);
      return;
    }

    setTimesheetsLoading(true);
    setTimesheetError("");

    try {
      const currentMonthKey = toMonthKey(new Date());
      const mission = missions.find((item) => item.id === missionId) || null;
      const monthsResponse = await listTimesheetMonths(missionId);
      const months = Array.isArray(monthsResponse) ? monthsResponse : [];
      const monthsByKey = new Map(
        months
          .map((entry) => [String(entry?.month || ""), entry])
          .filter(([monthKey]) => Boolean(monthKey)),
      );
      const availableMonths = getAvailableMonthsForTimesheet({
        contractStartDate: mission?.start_date,
        contractEndDate: mission?.end_date,
        now: new Date(),
      });
      const fallbackForecastDays = String(resolveMissionTimesheetFallbackDays(mission));
      setTimesheetRows(
        availableMonths.map((availableMonth) => {
          const month = monthsByKey.get(availableMonth.key) || {
            month: availableMonth.key,
            state: "OPEN",
            locked: false,
            timesheet_id: null,
            declared_days: null,
            forecast_days: resolveMissionTimesheetFallbackDays(mission),
          };

          return {
            key: month.month,
            label: formatMonthLabel(month.month),
            id: month.timesheet_id || null,
            locked: month.locked || month.state === "CLOSED",
            isFuture: month.month > currentMonthKey,
            isPast: month.month < currentMonthKey,
            isCurrent: month.month === currentMonthKey,
            days_declared:
              month.declared_days === null || month.declared_days === undefined
                ? ""
                : String(month.declared_days),
            days_forecast:
              month.forecast_days === null || month.forecast_days === undefined
                ? fallbackForecastDays
                : String(effectiveDays(null, month.forecast_days)),
            daily_entries: month.daily_entries || null,
          };
        }),
      );
    } catch (requestError) {
      if (process.env.NODE_ENV !== "production") {
        logAppError("collab.timesheets.load", requestError, {
          endpoint: "/admin/pilotage/timesheets/months",
          missionId,
          hasAuthToken: Boolean(token),
        });
      }

      setTimesheetError(
        getApiErrorMessage(requestError, "Impossible de charger vos jours."),
      );
    } finally {
      setTimesheetsLoading(false);
    }
  }, [missions, token]);

  const loadExpenses = useCallback(async (missionId) => {
    if (!missionId) {
      setExpenses([]);
      setExpensesLoading(false);
      return;
    }

    setExpensesLoading(true);
    setExpenseError("");

    try {
      const data = await listExpenses({ mission_id: missionId });
      setExpenses(data);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(requestError, "Impossible de charger vos frais."),
      );
    } finally {
      setExpensesLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadMissions();
  }, [loadMissions]);

  useEffect(() => {
    if (!selectedMissionId) {
      setTimesheetRows([]);
      setTimesheetsLoading(false);
      setExpenses([]);
      setExpensesLoading(false);
      return;
    }

    void loadTimesheets(selectedMissionId);
    void loadExpenses(selectedMissionId);
  }, [selectedMissionId, loadExpenses, loadTimesheets]);

  useEffect(() => {
    if (focusSection === "expenses") {
      expensesSectionRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [focusSection]);

  useEffect(() => {
    const unsubscribe = subscribeTimesheetUpdated((event) => {
      if (!selectedMissionId) {
        return;
      }

      if (event?.missionId && event.missionId !== selectedMissionId) {
        return;
      }

      void loadTimesheets(selectedMissionId);
    });

    return unsubscribe;
  }, [selectedMissionId, loadTimesheets]);

  const updateTimesheetDraft = (monthKey, field, value) => {
    setTimesheetRows((current) =>
      current.map((row) =>
        row.key === monthKey
          ? {
              ...row,
              [field]: value,
            }
          : row,
      ),
    );
    setTimesheetSaveStatus((prev) => ({ ...prev, [monthKey]: null }));
  };

  const updateTimesheetDailyEntries = (monthKey, daily_entries) => {
    // Also compute days_declared explicitly on front
    const total = Object.values(daily_entries).reduce((sum, val) => sum + (Number(val) || 0), 0);
    setTimesheetRows((current) =>
      current.map((row) =>
        row.key === monthKey
          ? {
              ...row,
              daily_entries: Object.keys(daily_entries).length > 0 ? daily_entries : null,
              days_declared: Object.keys(daily_entries).length > 0 ? String(total) : row.days_declared,
            }
          : row,
      ),
    );
    setTimesheetSaveStatus((prev) => ({ ...prev, [monthKey]: null }));
  };

  const saveTimesheet = async (row) => {
    setTimesheetBusyKey(row.key);
    setTimesheetError("");

    const payload = row.isFuture
      ? {
          days_forecast: effectiveDays(null, row.days_forecast),
        }
      : {
          days_declared:
            row.days_declared === "" ? null : Number(row.days_declared),
          days_forecast: effectiveDays(null, row.days_forecast),
          daily_entries: row.daily_entries,
        };

    try {
      if (row.id) {
        await updateTimesheet(row.id, payload);
      } else {
        await createTimesheet({
          mission_id: selectedMissionId,
          month: row.key,
          ...payload,
        });
      }

      emitTimesheetUpdated({
        collaboratorId: String(selectedMission?.collaborator_id || user?.id || ""),
        missionId: selectedMissionId,
        monthKey: row.key,
      });

      await loadTimesheets(selectedMissionId);
      setTimesheetSaveStatus((prev) => ({ ...prev, [row.key]: 'saved' }));
      setTimeout(() => {
        setTimesheetSaveStatus((prev) => ({ ...prev, [row.key]: null }));
      }, 3000);
    } catch (requestError) {
      setTimesheetError(
        getApiErrorMessage(requestError, "Impossible d'enregistrer vos jours."),
      );
      setTimesheetSaveStatus((prev) => ({ ...prev, [row.key]: 'error' }));
    } finally {
      setTimesheetBusyKey("");
    }
  };

  const handleExpenseSubmit = async (event) => {
    event.preventDefault();
    if (!selectedMissionId) {
      return;
    }

    setExpenseBusyId("create");
    setExpenseError("");

    try {
      await createExpense({
        mission_id: selectedMissionId,
        date: expenseForm.date,
        category: expenseForm.category,
        amount: Number(expenseForm.amount || 0),
        comment: expenseForm.comment.trim() || null,
      });

      setExpenseForm({
        date: new Date().toISOString().slice(0, 10),
        category: "TRANSPORT",
        amount: "",
        comment: "",
      });
      await loadExpenses(selectedMissionId);
    } catch (requestError) {
      setExpenseError(
        getApiErrorMessage(requestError, "Impossible d'ajouter votre frais."),
      );
    } finally {
      setExpenseBusyId("");
    }
  };

  const hasMissions = Boolean(missions.length);
  const selectedMissionLabel = getMissionDisplayName(selectedMission);

  return (
    <div className="space-y-6">
      <section className="rounded-3xl border border-white/10 glass-heavy p-6">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
              Collaborateur
            </p>
            <h1 className="mt-2 text-2xl font-heading font-bold text-white">
              Mon espace
            </h1>
            <p className="mt-2 text-sm text-slate-400">
              Retrouvez vos missions, saisissez vos jours et déclarez vos frais.
            </p>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] px-4 py-3 text-sm text-slate-300">
            {user?.name || user?.email}
          </div>
        </div>
      </section>

      {error ? <AppErrorState message={error} /> : null}

      <section
        className="rounded-3xl border border-white/10 glass p-6"
        data-testid="collab-missions-section"
      >
        <div className="flex items-center justify-between gap-3">
          <div>
            <h2 className="text-lg font-semibold text-white">Mes missions</h2>
            <p className="mt-1 text-sm text-slate-400">
              Cliquez sur une mission pour charger les jours et frais associés.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="mt-5">
            <AppLoadingState
              title="Chargement des missions"
              description="Récupération de vos missions actives."
              compact
            />
          </div>
        ) : null}

        {!loading && missions.length === 0 ? (
          <div className="mt-5">
            <AppEmptyState
              icon={BriefcaseBusiness}
              title="Aucune mission active."
              description="Votre espace personnel reste disponible pour consulter et déclarer vos frais."
            />
          </div>
        ) : null}

        <div className="mt-5 grid gap-4 lg:grid-cols-2">
          {missions.map((mission) => {
            const clientLabel = mission.client_name || "Intercontrat";
            const endLabel = mission.end_date
              ? formatDateLabel(mission.end_date)
              : "En cours";

            return (
              <button
                key={mission.id}
                type="button"
                onClick={() => setSelectedMissionId(mission.id)}
                className={`rounded-2xl border p-5 text-left transition-colors ${
                  selectedMissionId === mission.id
                    ? "border-lenvia-blue/40 bg-lenvia-blue/10"
                    : "border-white/10 bg-white/[0.03] hover:border-white/20"
                }`}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex h-10 w-10 items-center justify-center rounded-2xl bg-white/5 text-lenvia-blue">
                    <BriefcaseBusiness className="h-5 w-5" />
                  </div>
                  <span className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                    {mission.status}
                  </span>
                </div>
                <p className="mt-4 text-base font-semibold text-white">
                  {clientLabel}
                </p>
                <p className="mt-2 text-sm text-slate-400">
                  Du {formatDateLabel(mission.start_date)} au {endLabel}
                </p>
              </button>
            );
          })}
        </div>
      </section>

      {hasMissions ? (
        <section
          className="grid gap-6 xl:grid-cols-2"
          data-testid="collab-timesheets-section"
        >
          <div className="space-y-4 rounded-3xl border border-white/10 glass p-6">
            <div>
              <h2 className="text-lg font-semibold text-white">Mes jours</h2>
              <p className="mt-1 text-sm text-slate-400">
                Mission sélectionnée: {selectedMissionLabel}
              </p>
              {timesheetRows.some(r => r.daily_entries) && (
                <div className="mt-4 rounded-lg bg-lenvia-blue/10 border border-lenvia-blue/20 p-3 text-sm text-lenvia-blue">
                  ⓘ Vous utilisez la saisie détaillée. Le total mensuel est calculé automatiquement.
                </div>
              )}
            </div>

            {timesheetError ? (
              <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                <span>{timesheetError}</span>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    void loadTimesheets(selectedMissionId);
                  }}
                  className="border-red-400/30 text-red-100 hover:bg-red-500/10 hover:text-white"
                >
                  Réessayer
                </Button>
              </div>
            ) : null}

            {!timesheetsLoading && !timesheetError && timesheetRows.length === 0 ? (
              <AppEmptyState
                icon={BriefcaseBusiness}
                title="Aucun mois déclarable"
                description="Les mois suivent la règle contrat + année courante."
                className="p-6"
                testId="collab-timesheets-empty-state"
              />
            ) : (
              <PilotageTable
                columns={[
                  {
                    key: "label",
                    label: "Mois",
                    render: (row) => row.label,
                  },
                  {
                    key: "days_declared",
                    label: "Déclarés",
                    render: (row) => (
                      <div className="flex items-center gap-2">
                        {row.daily_entries ? (
                          <div className="flex items-center h-10 w-24 bg-lenvia-blue/5 border border-lenvia-blue/20 rounded-md px-3 text-sm font-medium text-white/80 cursor-not-allowed">
                            {row.days_declared} <span className="text-slate-500 ml-1 text-xs">jrs (auto)</span>
                          </div>
                        ) : (
                          <Input
                            type="number"
                            min="0"
                            step="0.5"
                            value={row.days_declared}
                            onChange={(event) =>
                              updateTimesheetDraft(row.key, "days_declared", event.target.value)
                            }
                            disabled={row.isFuture || row.locked || !selectedMissionId}
                            className="border-white/10 bg-slate-950 text-white disabled:opacity-60 w-24"
                          />
                        )}
                        {!row.isFuture && selectedMissionId && (
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className={`h-9 px-3 ${row.daily_entries ? 'text-lenvia-blue bg-lenvia-blue/10 hover:bg-lenvia-blue/20 border border-lenvia-blue/30' : 'text-slate-400 hover:text-white hover:bg-white/10 border border-transparent'}`}
                            onClick={() => setDailyModalRow(row)}
                            disabled={row.locked}
                          >
                            Détail
                          </Button>
                        )}
                      </div>
                    ),
                  },
                  {
                    key: "days_forecast",
                    label: "Prévision",
                    render: (row) => (
                      <Input
                        type="number"
                        min="0"
                        step="0.5"
                        value={row.days_forecast}
                        onChange={(event) =>
                          updateTimesheetDraft(row.key, "days_forecast", event.target.value)
                        }
                        disabled={!row.isFuture || !selectedMissionId}
                        className="border-white/10 bg-slate-950 text-white disabled:opacity-60"
                      />
                    ),
                  },
                ]}
                rows={timesheetRows}
                loading={timesheetsLoading}
                emptyMessage="Sélectionnez une mission."
                renderActions={(row) => {
                  const isSaving = timesheetBusyKey === row.key;
                  const isSaved = timesheetSaveStatus[row.key] === 'saved';
                  const isError = timesheetSaveStatus[row.key] === 'error';
                  
                  let buttonClass = "border-white/10 text-slate-200 hover:bg-white/5 hover:text-white";
                  let buttonText = "Enregistrer";
                  
                  if (isSaving) {
                    buttonText = "En cours...";
                    buttonClass = "border-amber-500/50 text-amber-500 bg-amber-500/10 hover:bg-amber-500/10 text-xs px-2";
                  } else if (isSaved) {
                    buttonText = "Sauvegardé";
                    buttonClass = "border-emerald-500/50 text-emerald-400 bg-emerald-500/10 hover:bg-emerald-500/10 hover:text-emerald-400 text-xs px-2";
                  } else if (isError) {
                    buttonText = "Erreur";
                    buttonClass = "border-rose-500/50 text-rose-400 bg-rose-500/10 hover:bg-rose-500/10 hover:text-rose-400 text-xs px-2";
                  }

                  return (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={
                      !selectedMissionId ||
                      row.locked ||
                      (row.isPast && row.days_declared === "" && !row.id) ||
                      isSaving
                    }
                    onClick={() => saveTimesheet(row)}
                    className={buttonClass}
                  >
                    {buttonText}
                  </Button>
                )}}
              />
            )}
          </div>

          <div
            ref={expensesSectionRef}
            className="space-y-4 rounded-3xl border border-white/10 glass p-6"
          >
            <div>
              <h2 className="text-lg font-semibold text-white">Mes frais</h2>
              <p className="mt-1 text-sm text-slate-400">
                Déclaration sur {selectedMissionLabel.toLowerCase()}.
              </p>
            </div>

            {expenseError ? (
              <div className="rounded-2xl border border-red-500/20 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {expenseError}
              </div>
            ) : null}

            <form className="grid gap-4" onSubmit={handleExpenseSubmit}>
              <div className="grid gap-4 md:grid-cols-3">
                <PilotageField label="Date" htmlFor="collab-expense-date">
                  <Input
                    id="collab-expense-date"
                    type="date"
                    value={expenseForm.date}
                    onChange={(event) =>
                      setExpenseForm((current) => ({
                        ...current,
                        date: event.target.value,
                      }))
                    }
                    required
                    disabled={!selectedMissionId}
                  />
                </PilotageField>

                <PilotageField label="Catégorie" htmlFor="collab-expense-category">
                  <select
                    id="collab-expense-category"
                    value={expenseForm.category}
                    onChange={(event) =>
                      setExpenseForm((current) => ({
                        ...current,
                        category: event.target.value,
                      }))
                    }
                    className="flex h-9 w-full rounded-md border border-white/10 bg-slate-950 px-3 py-1 text-sm text-white focus:outline-none focus:ring-1 focus:ring-lenvia-blue disabled:opacity-60"
                    disabled={!selectedMissionId}
                  >
                    {EXPENSE_CATEGORIES.map((category) => (
                      <option key={category} value={category}>
                        {category}
                      </option>
                    ))}
                  </select>
                </PilotageField>

                <PilotageField label="Montant" htmlFor="collab-expense-amount">
                  <Input
                    id="collab-expense-amount"
                    type="number"
                    min="0"
                    step="0.01"
                    value={expenseForm.amount}
                    onChange={(event) =>
                      setExpenseForm((current) => ({
                        ...current,
                        amount: event.target.value,
                      }))
                    }
                    required
                    disabled={!selectedMissionId}
                  />
                </PilotageField>
              </div>

              <PilotageField label="Commentaire" htmlFor="collab-expense-comment">
                <Textarea
                  id="collab-expense-comment"
                  value={expenseForm.comment}
                  onChange={(event) =>
                    setExpenseForm((current) => ({
                      ...current,
                      comment: event.target.value,
                    }))
                  }
                  placeholder="Contexte du frais"
                  className="border-white/10 bg-slate-950 text-white"
                  disabled={!selectedMissionId}
                />
              </PilotageField>

              <Button
                type="submit"
                className="btn-primary"
                disabled={!selectedMissionId || expenseBusyId === "create"}
              >
                {expenseBusyId === "create" ? "Ajout..." : "Ajouter un frais"}
              </Button>
            </form>

            <PilotageTable
              columns={[
                {
                  key: "date",
                  label: "Date",
                  render: (expense) => formatDateLabel(expense.date),
                },
                {
                  key: "category",
                  label: "Catégorie",
                  render: (expense) => expense.category,
                },
                {
                  key: "amount",
                  label: "Montant",
                  render: (expense) => formatCurrency(expense.amount),
                },
                {
                  key: "status",
                  label: "Statut",
                  render: (expense) => expense.status,
                },
              ]}
              rows={expenses}
              loading={expensesLoading}
              emptyMessage={
                selectedMission
                  ? "Aucun frais déclaré."
                  : "Sélectionnez une mission pour voir vos frais."
              }
            />
          </div>
        </section>
      ) : (
        <section
          ref={expensesSectionRef}
          className="space-y-4 rounded-3xl border border-white/10 glass p-6"
          data-testid="collab-expenses-section"
        >
          <div>
            <h2 className="text-lg font-semibold text-white">Mes frais</h2>
            <p className="mt-1 text-sm text-slate-400">
              L'espace dépenses est accessible dès qu'une mission est active.
            </p>
          </div>

          <AppEmptyState
            icon={BriefcaseBusiness}
            title="Aucune mission active"
            description="Les frais restent visibles ici, mais aucune mission rattachée n'est disponible pour le moment."
            className="p-6"
            testId="collab-no-mission-empty-state"
          />
        </section>
      )}

      {/* MODAL */}
      <TimesheetDailyModal
        isOpen={Boolean(dailyModalRow)}
        onClose={() => setDailyModalRow(null)}
        monthKey={dailyModalRow?.key}
        initialEntries={dailyModalRow?.daily_entries}
        isLocked={dailyModalRow?.locked}
        onSave={(entries) => updateTimesheetDailyEntries(dailyModalRow.key, entries)}
      />
    </div>
  );
};

export default CollabHome;
